import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import "../../styles/print.css";

/**
 * 만족도 조사 상세 인쇄 전용 레이아웃
 * - 화면에서는 숨김(.print-only), window.print() 시에만 출력됩니다.
 * @param {Object} detail - POST /users/detail/satisfaction-survey-detail 응답 데이터
 * @param {String} participantLabel - 참가자 표기 (예: "홍길동(32세) 남")
 * @param {String} roundLabel - 회차 표기 (예: "3차")
 */

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10);
  return parsed.toLocaleDateString("sv-SE");
};

const renderValue = (value) => {
  if (value == null || value === "") return "-";

  if (Array.isArray(value)) {
    if (value.length === 0) return "-";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {value.map((item, idx) => (
          <Box key={idx}>{typeof item === "object" ? renderValue(item) : String(item)}</Box>
        ))}
      </Box>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length === 0) return "-";
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {entries.map(([key, val]) => (
          <Box key={key}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {key}
            </Typography>
            <Box>{renderValue(val)}</Box>
          </Box>
        ))}
      </Box>
    );
  }

  return String(value);
};

const renderAnswer = (answer, idx) => {
  if (answer == null || answer === "") return null;

  if (typeof answer !== "object") {
    return (
      <Box key={idx} className="print-question" sx={{ py: 1, borderBottom: "1px solid #ccc" }}>
        <Typography variant="body2">{String(answer)}</Typography>
      </Box>
    );
  }

  const question =
    answer.question || answer.question_title || answer.title || answer.item || `문항 ${idx + 1}`;
  const response =
    answer.answer ?? answer.response ?? answer.value ?? answer.content ?? answer.text ?? "-";

  return (
    <Box key={answer.id || idx} className="print-question" sx={{ py: 1, borderBottom: "1px solid #ccc" }}>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Q{idx + 1}. {question}
      </Typography>
      <Box sx={{ fontSize: "14px" }}>
        A. {typeof response === "object" ? renderValue(response) : String(response)}
      </Box>
    </Box>
  );
};

const SatisfactionSurveyDetailPrint = ({ detail, participantLabel, roundLabel }) => {
  if (!detail) return null;

  const answers = Array.isArray(detail.answers) ? detail.answers : [];

  return (
    <Box className="satisfaction-detail-print-root print-only">
      <Box sx={{ pb: 1.5, mb: 2, borderBottom: "2px solid #333" }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {detail.title || "만족도 조사"} {roundLabel ? `(${roundLabel})` : ""}
        </Typography>
        <Typography variant="body2">{participantLabel}</Typography>
        <Typography variant="caption">완료일: {formatDate(detail.completed_at)}</Typography>
      </Box>

      {answers.length > 0 ? (
        answers.map(renderAnswer)
      ) : (
        <Typography variant="body2">답변 내역이 없습니다.</Typography>
      )}
    </Box>
  );
};

export default SatisfactionSurveyDetailPrint;
